// Copyright 2020-2021 the .NET Foundation
// Licensed under the MIT License
/** Type guard function for AddLinePointMessage. */
export function isAddLinePointMessage(o) {
    return typeof o.event === "string" &&
        o.event == "line_add_point" &&
        typeof o.id === "string" &&
        typeof o.ra === "number" &&
        typeof o.dec === "number";
}
/** Type guard function for AddPolygonPointMessage. */
export function isAddPolygonPointMessage(o) {
    return typeof o.event === "string" &&
        o.event == "polygon_add_point" &&
        typeof o.id === "string" &&
        typeof o.ra === "number" &&
        typeof o.dec === "number";
}
/** Type guard function for CenterOnCoordinatesMessage. */
export function isCenterOnCoordinatesMessage(o) {
    return typeof o.event === "string" &&
        o.event == "center_on_coordinates" &&
        typeof o.ra === "number" &&
        typeof o.dec === "number" &&
        typeof o.fov === "number" &&
        typeof o.instant === "boolean" &&
        (o.roll === undefined || typeof o.roll === "number");
}
/** Type guard function for ClearAnnotationsMessage. */
export function isClearAnnotationsMessage(o) {
    return typeof o.event === "string" &&
        o.event == "clear_annotations";
}
/** Type guard function for CreateAnnotationMessage. */
export function isCreateAnnotationMessage(o) {
    return typeof o.event === "string" &&
        o.event == "annotation_create" &&
        typeof o.shape === "string" &&
        (o.shape == "circle" || o.shape == "line" || o.shape == "polygon") &&
        typeof o.id === "string";
}
/** Type guard function for [[CreateImageSetLayerMessage]]. */
export function isCreateImageSetLayerMessage(o) {
    return typeof o.event === "string" &&
        o.event == "image_layer_create" &&
        typeof o.id === "string" &&
        typeof o.url === "string" &&
        (o.mode == "autodetect" || o.mode == "fits" || o.mode == "preloaded") &&
        (o.goto === undefined || typeof o.goto === "boolean") &&
        (o.name === undefined || typeof o.name === "string");
}
/** Type guard function for [[CreateFitsLayerMessage]].
 *
 * These messages may not have a `mode` field, so that they can be distinguished
 * from [[CreateImageSetLayerMessage]] instances.
 * */
export function isCreateFitsLayerMessage(o) {
    return typeof o.event === "string" &&
        o.event == "image_layer_create" &&
        typeof o.id === "string" &&
        typeof o.url === "string" &&
        typeof o.mode === "undefined";
}
/** Type guard function for SetLayerOrderMessage. */
export function isSetLayerOrderMessage(o) {
    return typeof o.event === "string" &&
        o.event == "image_layer_order" &&
        typeof o.id === "string" &&
        typeof o.version === "number" &&
        typeof o.order === "number";
}
/** Type guard function for CreateTableLayerMessage. */
export function isCreateTableLayerMessage(o) {
    return typeof o.event === "string" &&
        o.event == "table_layer_create" &&
        typeof o.id === "string" &&
        typeof o.table === "string" &&
        typeof o.frame === "string";
}
/** Type guard function for LoadImageCollectionMessage. */
export function isLoadImageCollectionMessage(o) {
    return typeof o.event === "string" &&
        o.event == "load_image_collection" &&
        typeof o.url === "string" &&
        (o.loadChildFolders === undefined || typeof o.loadChildFolders === "boolean") &&
        (o.threadId === undefined || typeof o.threadId === "string");
}
/** Type guard function for [[LoadImageCollectionCompletedMessage]]. */
export function isLoadImageCollectionCompletedMessage(o) {
    return typeof o.event === "string" &&
        o.event == "load_image_collection_completed" &&
        typeof o.url === "string" &&
        (o.threadId === undefined || typeof o.threadId === "string");
}
/** Type guard function for LoadTourMessage. */
export function isLoadTourMessage(o) {
    return typeof o.event === "string" &&
        o.event == "load_tour" &&
        typeof o.url === "string";
}
/** Type guard function for ModifyAnnotationMessage. */
export function isModifyAnnotationMessage(o) {
    return typeof o.event === "string" &&
        o.event == "annotation_set" &&
        typeof o.id === "string" &&
        typeof o.setting === "string" &&
        typeof o.value !== "undefined"; // note that this one is unusual
}
/** Type guard function for ModifyFitsLayerMessage. */
export function isModifyFitsLayerMessage(o) {
    return typeof o.event === "string" &&
        o.event == "image_layer_set" &&
        typeof o.id === "string" &&
        typeof o.setting === "string" &&
        typeof o.value !== "undefined"; // note that this one is unusual
}
/** Type guard function for ModifySettingMessage. */
export function isModifySettingMessage(o) {
    return typeof o.event === "string" &&
        o.event == "setting_set" &&
        typeof o.setting === "string" &&
        typeof o.value !== "undefined"; // note that this one is unusual
}
/** Type guard function for ModifyTableLayerMessage. */
export function isModifyTableLayerMessage(o) {
    return typeof o.event === "string" &&
        o.event == "table_layer_set" &&
        typeof o.id === "string" &&
        typeof o.setting === "string" &&
        typeof o.value !== "undefined"; // note that this one is unusual
}
/** Type guard function for PauseTimeMessage. */
export function isPauseTimeMessage(o) {
    return typeof o.event === "string" &&
        o.event == "pause_time";
}
/** Type guard function for PauseTourMessage. */
export function isPauseTourMessage(o) {
    return typeof o.event === "string" &&
        o.event == "pause_tour";
}
/** Type guard function for RemoveAnnotationMessage. */
export function isRemoveAnnotationMessage(o) {
    return typeof o.event === "string" &&
        o.event == "remove_annotation" &&
        typeof o.id === "string";
}
/** Type guard function for RemoveImageSetLayerMessage. */
export function isRemoveImageSetLayerMessage(o) {
    return typeof o.event === "string" &&
        o.event == "image_layer_remove" &&
        typeof o.id === "string";
}
/** Type guard function for RemoveTableLayerMessage. */
export function isRemoveTableLayerMessage(o) {
    return typeof o.event === "string" &&
        o.event == "table_layer_remove" &&
        typeof o.id === "string";
}
/** Type guard function for ResumeTimeMessage. */
export function isResumeTimeMessage(o) {
    return typeof o.event === "string" &&
        o.event == "resume_time" &&
        typeof o.rate === "number";
}
/** Type guard function for ResumeTourMessage. */
export function isResumeTourMessage(o) {
    return typeof o.event === "string" &&
        o.event == "resume_tour";
}
/** Type guard function for SetBackgroundByNameMessage. */
export function isSetBackgroundByNameMessage(o) {
    return typeof o.event === "string" &&
        o.event == "set_background_by_name" &&
        typeof o.name === "string";
}
/** Type guard function for SetCircleCenterMessage. */
export function isSetCircleCenterMessage(o) {
    return typeof o.event === "string" &&
        o.event == "circle_set_center" &&
        typeof o.id === "string" &&
        typeof o.ra === "number" &&
        typeof o.dec === "number";
}
/** Type guard function for SetDatetimeMessage. */
export function isSetDatetimeMessage(o) {
    return typeof o.event === "string" &&
        o.event == "set_datetime" &&
        typeof o.isot === "string";
}
/** Type guard function for SetFitsLayerColormapMessage. */
export function isSetFitsLayerColormapMessage(o) {
    return typeof o.event === "string" &&
        o.event == "image_layer_cmap" &&
        typeof o.id === "string" &&
        typeof o.version === "number" &&
        typeof o.cmap === "string";
}
/** Type guard function for SetForegroundByNameMessage. */
export function isSetForegroundByNameMessage(o) {
    return typeof o.event === "string" &&
        o.event == "set_foreground_by_name" &&
        typeof o.name === "string";
}
/** Type guard function for SetForegroundOpacityMessage. */
export function isSetForegroundOpacityMessage(o) {
    return typeof o.event === "string" &&
        o.event == "set_foreground_opacity" &&
        typeof o.value === "number";
}
/** Type guard function for SetViewerModeMessage. */
export function isSetViewerModeMessage(o) {
    return typeof o.event === "string" &&
        o.event == "set_viewer_mode" &&
        typeof o.mode === "string";
}
/** Type guard function for StretchFitsLayerMessage. */
export function isStretchFitsLayerMessage(o) {
    return typeof o.event === "string" &&
        o.event == "image_layer_stretch" &&
        typeof o.id === "string" &&
        typeof o.version === "number" &&
        typeof o.stretch === "number" &&
        typeof o.vmin === "number" &&
        typeof o.vmax === "number";
}
/** Type guard function for TrackObjectMessage. */
export function isTrackObjectMessage(o) {
    return typeof o.event === "string" &&
        o.event == "track_object" &&
        typeof o.code === "number";
}
/** Type guard function for UpdateTableLayerMessage. */
export function isUpdateTableLayerMessage(o) {
    return typeof o.event === "string" &&
        o.event == "table_layer_update" &&
        typeof o.id === "string" &&
        typeof o.table === "string";
}
/** If the input object is a classic pywwt message, rewrite any relative URLs
 * that it contains.
 *
 * The motivation for this function is to allow data exchange from a
 * Jupyter(Lab) server to the WWT research app. It is possible to publish data
 * by hooking into the Jupyter server, but that layer of code doesn't always
 * know the URL at which it is exposed to the world. So, such layers need to
 * supply relative URLs in their messages, and then the JavaScript layer needs
 * to modify those messages to turn them into absolute URLs. That's what this
 * function enables. The input is modified in-place.
*/
export function applyBaseUrlIfApplicable(o, baseurl) {
    // Creating a URL object can cause extra escaping to be applied, which can
    // break some WWT engine APIs that expect to match URLs with exact string
    // comparisons. In particular, braces will get percent-escaped, which causes
    // problems for tiled imagery. I'm not aware of a better solution than to
    // blindly undo the escaping that we *probably* don't want.
    function rewrite(url) {
        const u = new URL(url, baseurl);
        return u.toString().replace(/%7B/g, '{').replace(/%7D/g, '}');
    }
    if (isCreateImageSetLayerMessage(o)) {
        o.url = rewrite(o.url);
    }
    else if (isCreateFitsLayerMessage(o)) {
        o.url = rewrite(o.url);
    }
    else if (isLoadImageCollectionMessage(o)) {
        o.url = rewrite(o.url);
    }
    else if (isLoadTourMessage(o)) {
        o.url = rewrite(o.url);
    }
}
const pywwtSpreadSheetLayerSettingTypeInfo = {
    "altColumn/string": true,
    "altColumn/number": true,
    "altType/string": true,
    "altUnit/string": true,
    "astronomical/boolean": true,
    "barChartBitmask/number": true,
    "beginRange/Date": true,
    "cartesianCustomScale/number": true,
    "cartesianScale/string": true,
    "color/string": true,
    "colorMapColumn/string": true,
    "colorMapColumn/number": true,
    "colorMapperName/string": true,
    "coordinatesType/string": true,
    "decay/number": true,
    "dynamicColor/boolean": true,
    "dynamicData/boolean": true,
    "enabled/boolean": true,
    "endDateColumn/string": true,
    "endDateColumn/number": true,
    "endRange/Date": true,
    "fadeSpan/number": true,
    "geometryColumn/string": true,
    "geometryColumn/number": true,
    "hyperlinkColumn/string": true,
    "hyperlinkColumn/number": true,
    "hyperlinkFormat/string": true,
    "latColumn/string": true,
    "latColumn/number": true,
    "lngColumn/string": true,
    "lngColumn/number": true,
    "markerColumn/string": true,
    "markerColumn/number": true,
    "markerIndex/number": true,
    "markerScale/string": true,
    "name/string": true,
    "nameColumn/string": true,
    "nameColumn/number": true,
    "normalizeColorMap/boolean": true,
    "normalizeColorMapMax/number": true,
    "normalizeColorMapMin/number": true,
    "normalizeSize/boolean": true,
    "normalizeSizeClip/boolean": true,
    "normalizeSizeMax/number": true,
    "normalizeSizeMin/number": true,
    "opacity/number": true,
    "opened/boolean": true,
    "plotType/string": true,
    "pointScaleType/number": true,
    "raUnits/string": true,
    "referenceFrame/string": true,
    "scaleFactor/number": true,
    "showFarSide/boolean": true,
    "sizeColumn/string": true,
    "sizeColumn/number": true,
    "startDateColumn/string": true,
    "startDateColumn/number": true,
    "timeSeries/boolean": true,
    "version/number": true,
    "xAxisColumn/string": true,
    "xAxisColumn/number": true,
    "xAxisReverse/boolean": true,
    "yAxisColumn/string": true,
    "yAxisColumn/number": true,
    "yAxisReverse/boolean": true,
    "zAxisColumn/string": true,
    "zAxisColumn/number": true,
    "zAxisReverse/boolean": true,
};
/** Type guard function for PywwtSpreadSheetSetting. */
export function isPywwtSpreadSheetLayerSetting(obj) {
    const key = obj[0] + "/" + typeof obj[1];
    return (key in pywwtSpreadSheetLayerSettingTypeInfo);
}
